"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "../DataTable";
import { useERP, formatBDT, formatNum } from "@/lib/erp/store";
import { ShoppingCart, Globe, Factory, FileText, Truck, Package, Leaf, TrendingUp, Package2, Boxes, BarChart3 } from "lucide-react";

function PageHeader({ title, desc, icon: Icon }: { title: string; desc: string; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex items-start gap-3">
      <div className="bg-primary text-primary-foreground flex size-10 items-center justify-center rounded-lg"><Icon className="size-5" /></div>
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">{title}</h1>
        <p className="text-muted-foreground mt-1 text-sm">{desc}</p>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{label}</CardTitle></CardHeader>
      <CardContent><div className={`text-2xl font-semibold ${tone || ""}`}>{value}</div></CardContent>
    </Card>
  );
}

export function EcommerceModule() {
  const erp = useERP();
  const orders = erp.salesOrders.slice(0, 5).map(o => ({
    id: o.id, order: `WEB-${o.soNumber.split("-").pop()}`, customer: erp.getCustomer(o.customerId)?.name ?? "—",
    channel: Math.random() > 0.5 ? "Web" : "App", total: o.total, status: o.status,
  }));
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="E-Commerce Module" desc="Online storefront, orders, fulfillment integration." icon={Globe} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Online Orders" value={formatNum(orders.length)} />
        <StatCard label="Web Revenue" value={formatBDT(orders.reduce((s, r) => s + r.total, 0))} tone="text-primary" />
        <StatCard label="Avg Order Value" value={formatBDT(orders.reduce((s, r) => s + r.total, 0) / Math.max(1, orders.length))} />
        <StatCard label="Cart Abandonment" value="24.5%" tone="text-destructive" />
      </div>
      <Card>
        <CardHeader><CardTitle>Online Orders</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "order", header: "Order #", sortable: true, render: r => <span className="font-mono text-xs">{r.order}</span> },
              { key: "customer", header: "Customer" },
              { key: "channel", header: "Channel", render: r => <Badge variant="secondary">{r.channel}</Badge> },
              { key: "total", header: "Total", align: "right", sortable: true, render: r => formatBDT(r.total) },
              { key: "status", header: "Status", render: r => <Badge variant="secondary">{r.status}</Badge> },
            ] as Column<typeof orders[number]>[]}
            data={orders}
            rowKey={r => r.id}
            searchKeys={["order", "customer"]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export function ManufacturingModule() {
  const rows = [
    { id: "mo1", mo: "MO-2026-0142", product: "Spice Mix 200g (Bulk)", qty: 5000, status: "In Progress", start: "2026-10-12", end: "2026-10-15" },
    { id: "mo2", mo: "MO-2026-0141", product: "Tea Grade A 500g (Pack)", qty: 2400, status: "Completed", start: "2026-10-10", end: "2026-10-12" },
    { id: "mo3", mo: "MO-2026-0140", product: "Flour 10kg", qty: 800, status: "Planned", start: "2026-10-15", end: "2026-10-17" },
  ];
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Manufacturing Module" desc="Production orders, BOM, work centers and yield tracking." icon={Factory} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active MOs" value={formatNum(rows.filter(r => r.status === "In Progress").length)} />
        <StatCard label="Completed" value={formatNum(rows.filter(r => r.status === "Completed").length)} tone="text-primary" />
        <StatCard label="Planned" value={formatNum(rows.filter(r => r.status === "Planned").length)} />
        <StatCard label="Avg Yield" value="94.2%" tone="text-primary" />
      </div>
      <Card>
        <CardHeader><CardTitle>Manufacturing Orders</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "mo", header: "MO #", sortable: true, render: r => <span className="font-mono text-xs">{r.mo}</span> },
              { key: "product", header: "Product" },
              { key: "qty", header: "Qty", align: "right" },
              { key: "start", header: "Start" },
              { key: "end", header: "End" },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Completed" ? "default" : r.status === "In Progress" ? "secondary" : "outline"}>{r.status}</Badge> },
            ] as Column<typeof rows[number]>[]}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["mo", "product"]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export function ProjectSalesModule() {
  const rows = [
    { id: "pr1", code: "PRJ-2026-018", name: "Corporate Canteen Supply — BTCL", client: "BTCL", value: 4800000, progress: 65, status: "Active" },
    { id: "pr2", code: "PRJ-2026-017", name: "Hospital Bulk Order — Square Hospitals", client: "Square Hospitals", value: 2850000, progress: 100, status: "Closed" },
  ];
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Project Sales Module" desc="Bid management, milestone billing and project profitability." icon={FileText} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active Projects" value={formatNum(rows.filter(r => r.status === "Active").length)} />
        <StatCard label="Project Pipeline" value={formatBDT(rows.reduce((s, r) => s + r.value, 0))} tone="text-primary" />
        <StatCard label="Avg Progress" value={`${Math.round(rows.reduce((s, r) => s + r.progress, 0) / rows.length)}%`} />
        <StatCard label="Closed" value={formatNum(rows.filter(r => r.status === "Closed").length)} />
      </div>
      <Card>
        <CardHeader><CardTitle>Active Projects</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "code", header: "Project #", sortable: true, render: r => <span className="font-mono text-xs">{r.code}</span> },
              { key: "name", header: "Project Name" },
              { key: "client", header: "Client" },
              { key: "value", header: "Value", align: "right", sortable: true, render: r => formatBDT(r.value) },
              { key: "progress", header: "Progress", align: "right", render: r => `${r.progress}%` },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Active" ? "default" : "secondary"}>{r.status}</Badge> },
            ] as Column<typeof rows[number]>[]}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["code", "name", "client"]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export function ThreePLModule() {
  const erp = useERP();
  const rows = erp.warehouses.map(w => ({
    id: w.id, wh: w.code, client: "External Client " + w.code.slice(-1),
    inbound: Math.floor(Math.random() * 200 + 50), outbound: Math.floor(Math.random() * 150 + 30),
    storage: w.utilization, revenue: w.capacity * 12,
  }));
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="3PL Management" desc="Third-party logistics operations, billing and SLA tracking." icon={Truck} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="3PL Clients" value={formatNum(rows.length)} />
        <StatCard label="Total Inbound" value={formatNum(rows.reduce((s, r) => s + r.inbound, 0))} />
        <StatCard label="Total Outbound" value={formatNum(rows.reduce((s, r) => s + r.outbound, 0))} />
        <StatCard label="Monthly Revenue" value={formatBDT(rows.reduce((s, r) => s + r.revenue, 0))} tone="text-primary" />
      </div>
      <Card>
        <CardHeader><CardTitle>3PL Operations</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "wh", header: "Warehouse", sortable: true, render: r => <span className="font-mono text-xs">{r.wh}</span> },
              { key: "client", header: "Client" },
              { key: "inbound", header: "Inbound", align: "right" },
              { key: "outbound", header: "Outbound", align: "right" },
              { key: "storage", header: "Storage %", align: "right", render: r => `${r.storage}%` },
              { key: "revenue", header: "Revenue", align: "right", sortable: true, render: r => formatBDT(r.revenue) },
            ] as Column<typeof rows[number]>[]}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["wh", "client"]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export function ConsignmentModule() {
  const erp = useERP();
  const rows = erp.customers.slice(0, 6).map((c, i) => ({
    id: c.id, code: c.code, name: c.name,
    stockOut: 100 + i * 50, sold: 80 + i * 40, returned: 5 + i * 2,
    settlement: (80 + i * 40) * 220,
  }));
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Consignment Module" desc="Consigner/consignee stock tracking and settlement." icon={Package} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Consignment Partners" value={formatNum(rows.length)} />
        <StatCard label="Total Stock-Out" value={formatNum(rows.reduce((s, r) => s + r.stockOut, 0))} />
        <StatCard label="Total Sold" value={formatNum(rows.reduce((s, r) => s + r.sold, 0))} tone="text-primary" />
        <StatCard label="Pending Settlement" value={formatBDT(rows.reduce((s, r) => s + r.settlement, 0))} />
      </div>
      <Card>
        <CardHeader><CardTitle>Consignment Stock</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{r.code}</span> },
              { key: "name", header: "Partner" },
              { key: "stockOut", header: "Stock-Out", align: "right" },
              { key: "sold", header: "Sold", align: "right", sortable: true },
              { key: "returned", header: "Returned", align: "right" },
              { key: "settlement", header: "Settlement", align: "right", sortable: true, render: r => formatBDT(r.settlement) },
            ] as Column<typeof rows[number]>[]}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["code", "name"]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export function ESGModule() {
  const rows = [
    { id: "e1", metric: "Carbon Footprint", value: "182 tCO2e", target: "150 tCO2e", status: "Above Target" },
    { id: "e2", metric: "Energy Consumption", value: "24,500 kWh", target: "20,000 kWh", status: "Above Target" },
    { id: "e3", metric: "Water Usage", value: "1,240 m³", target: "1,500 m³", status: "On Target" },
    { id: "e4", metric: "Waste Recycled", value: "82%", target: "80%", status: "On Target" },
    { id: "e5", metric: "Community Investment", value: "৳ 4.2M", target: "৳ 4.0M", status: "On Target" },
  ];
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="ESG & Sustainability" desc="Environmental, Social and Governance metrics tracking." icon={Leaf} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Carbon Offset" value="48 tCO2e" tone="text-primary" />
        <StatCard label="Recycling Rate" value="82%" tone="text-primary" />
        <StatCard label="Community Spend" value="৳4.2M" />
        <StatCard label="Diversity Index" value="0.78" />
      </div>
      <Card>
        <CardHeader><CardTitle>ESG Scorecard</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "metric", header: "Metric", sortable: true },
              { key: "value", header: "Current" },
              { key: "target", header: "Target" },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "On Target" ? "default" : "destructive"}>{r.status}</Badge> },
            ] as Column<typeof rows[number]>[]}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["metric"]}
          />
        </CardContent>
      </Card>
    </div>
  );
}
